import {
  Box,
  createListCollection,
  Field,
  Flex,
  Heading,
  Input,
  Portal,
  Select,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';
import { withMask } from 'use-mask-input';

import { emptyPersonalInfoFormState, PersonalInfoFormState } from '@features/onboarding/kycTypes';

const SUPPORTED_COUNTRIES = createListCollection({
  items: [{ value: 'GH', label: 'Ghana' }],
});

const DOB_REGEX = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;

const isValidDate = (dob: string): boolean => {
  if (!DOB_REGEX.test(dob)) {
    return false;
  }
  const [month, day, year] = dob.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    date < new Date()
  );
};

const isFormValid = (state: PersonalInfoFormState): boolean => {
  if (
    state.legalFullName.trim() === '' ||
    state.dateOfBirth === '' ||
    !isValidDate(state.dateOfBirth) ||
    state.country === '' ||
    state.address.trim() === '' ||
    Object.values(state.errors).some((v) => v != null)
  ) {
    return false;
  }
  return true;
};

interface Props {
  onFormStateChange: (state: PersonalInfoFormState, isValid: boolean) => void;
}

const PersonalInfoStep = ({ onFormStateChange }: Props) => {
  const [formState, setFormState] = useState<PersonalInfoFormState>(emptyPersonalInfoFormState());

  const updateState = useCallback(
    (nextState: PersonalInfoFormState) => {
      setFormState(nextState);
      onFormStateChange(nextState, isFormValid(nextState));
    },
    [onFormStateChange],
  );

  const onChangeFullName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      updateState({
        ...formState,
        legalFullName: value,
        errors: {
          ...formState.errors,
          legalFullName: value.trim() ? undefined : 'Legal full name is required',
        },
      });
    },
    [formState, updateState],
  );

  const onBlurFullName = useCallback(() => {
    if (formState.legalFullName.trim() === '') {
      updateState({
        ...formState,
        errors: { ...formState.errors, legalFullName: 'Legal full name is required' },
      });
    }
  }, [formState, updateState]);

  const onChangeDob = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateState({
        ...formState,
        dateOfBirth: e.target.value,
        errors: { ...formState.errors, dateOfBirth: undefined },
      });
    },
    [formState, updateState],
  );

  const onBlurDob = useCallback(() => {
    const { dateOfBirth } = formState;
    let err: string | undefined;
    if (dateOfBirth === '') {
      err = 'Date of birth is required';
    } else if (!isValidDate(dateOfBirth)) {
      err = 'Enter a valid date in MM/DD/YYYY format';
    }
    updateState({ ...formState, errors: { ...formState.errors, dateOfBirth: err } });
  }, [formState, updateState]);

  const onChangeCountry = useCallback(
    (details: { value: string[] }) => {
      const country = details.value[0] ?? '';
      const nextState: PersonalInfoFormState = {
        ...formState,
        country,
        errors: { ...formState.errors, country: country ? undefined : 'Country is required' },
      };
      setFormState(nextState);
      onFormStateChange(nextState, isFormValid(nextState));
    },
    [formState, onFormStateChange],
  );

  const onChangeAddress = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      updateState({
        ...formState,
        address: value,
        errors: { ...formState.errors, address: value.trim() ? undefined : 'Address is required' },
      });
    },
    [formState, updateState],
  );

  const onBlurAddress = useCallback(() => {
    if (formState.address.trim() === '') {
      updateState({
        ...formState,
        errors: { ...formState.errors, address: 'Address is required' },
      });
    }
  }, [formState, updateState]);

  return (
    <Flex direction="column" gap={6} maxW="lg">
      <Box>
        <Heading size="md">Personal Information</Heading>
        <Text color="text.secondary" fontSize="sm" fontWeight="medium" mt={1}>
          Provide your personal details to verify your identity.
        </Text>
      </Box>

      <Flex direction="column" gap={5}>
        <Field.Root invalid={!!formState.errors.legalFullName}>
          <Field.Label>Legal Full Name</Field.Label>
          <Input
            placeholder="Enter your legal full name"
            value={formState.legalFullName}
            borderColor="border.light"
            onChange={onChangeFullName}
            onBlur={onBlurFullName}
          />
          <Field.ErrorText>{formState.errors.legalFullName}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={!!formState.errors.dateOfBirth}>
          <Field.Label>Date of Birth</Field.Label>
          <Input
            placeholder="MM/DD/YYYY"
            ref={withMask('99/99/9999')}
            value={formState.dateOfBirth}
            borderColor="border.light"
            maxLength={10}
            onChange={onChangeDob}
            onBlur={onBlurDob}
          />
          <Field.HelperText fontSize="xs">Format: MM/DD/YYYY</Field.HelperText>
          <Field.ErrorText>{formState.errors.dateOfBirth}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={!!formState.errors.country}>
          <Field.Label>Country of Residence</Field.Label>
          <Select.Root collection={SUPPORTED_COUNTRIES} onValueChange={onChangeCountry} size="md">
            <Select.HiddenSelect />
            <Select.Control>
              <Select.Trigger borderColor="border.light">
                <Select.ValueText placeholder="Select your country" />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
              <Select.Positioner>
                <Select.Content bg="surface.background">
                  {SUPPORTED_COUNTRIES.items.map((item) => (
                    <Select.Item item={item} key={item.value}>
                      {item.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
          <Field.ErrorText>{formState.errors.country}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={!!formState.errors.address}>
          <Field.Label>Address</Field.Label>
          <Textarea
            placeholder="Enter your residential address"
            value={formState.address}
            borderColor="border.light"
            rows={3}
            onChange={onChangeAddress}
            onBlur={onBlurAddress}
          />
          <Field.ErrorText>{formState.errors.address}</Field.ErrorText>
        </Field.Root>
      </Flex>
    </Flex>
  );
};

export default observer(PersonalInfoStep);
